/**
 * API client.
 *
 * - Reads `VITE_API_BASE_URL` so the SPA can call the Function App directly
 *   (no SWA backend link). Falls back to relative `/api` for local dev when
 *   running behind `swa start`.
 * - For paths under `/api/mgmt/*` we attach `Authorization: Bearer <token>`
 *   from MSAL. Anonymous endpoints (e.g. `/api/events/published`,
 *   `/api/venues/public`, `/api/bootstrap`) are called without a token.
 *
 * Token acquisition is plugged in via `setTokenProvider()` so this module
 * stays free of MSAL imports (tree-shakable, easy to mock in tests).
 *
 * Cold-start resilience:
 *   The Function App runs on Flex Consumption and scales to zero. When the
 *   first request lands on a cold instance, the host answers CORS preflights
 *   (OPTIONS → 204) but returns an EMPTY-BODY 404 for real routes until the
 *   functions finish indexing (~10-40s), and may briefly return 502/503/504
 *   through the gateway. Without handling, the very first page load after a
 *   long absence shows a red "API 404" banner even though the app is fine a
 *   second later. `request()` retries idempotent GET/HEAD calls on these
 *   transient signals with capped exponential backoff. The app's OWN 404s
 *   always carry a JSON body (`{ error: 'not found' }`), so an empty-body 404
 *   is an unambiguous cold-start marker and never masks a real "not found".
 */

const BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

// Retry policy for cold-start / transient gateway failures on idempotent reads.
// 5 attempts with backoff (~0 + 0.5 + 1 + 2 + 4s ≈ up to ~7.5s of waiting) is
// comfortably longer than a typical Flex Consumption cold start while still
// failing fast enough that a genuinely-down API surfaces an error promptly.
const TRANSIENT_MAX_ATTEMPTS = 5;
const TRANSIENT_BASE_DELAY_MS = 500;
const TRANSIENT_MAX_DELAY_MS = 4000;

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

function isIdempotentMethod(method: string | undefined): boolean {
  const m = (method ?? 'GET').toUpperCase();
  return m === 'GET' || m === 'HEAD';
}

/**
 * True when an HTTP status + body looks like a Function App cold start or a
 * transient gateway hiccup, i.e. safe/worthwhile to retry on an idempotent
 * request. A 404 only qualifies when the body is empty — the host emits an
 * empty-body 404 while routes are still being indexed on a cold instance,
 * whereas the application's `notFound()` helper always returns a JSON body.
 */
function isTransientColdStart(status: number, bodyText: string): boolean {
  if (status === 502 || status === 503 || status === 504) return true;
  if (status === 404 && bodyText.trim() === '') return true;
  return false;
}

function backoffDelayMs(attemptIndex: number): number {
  // attemptIndex is 0-based; first retry waits BASE, then doubles, capped.
  const raw = TRANSIENT_BASE_DELAY_MS * 2 ** attemptIndex;
  return Math.min(raw, TRANSIENT_MAX_DELAY_MS);
}

type TokenProvider = () => Promise<string | null>;
let tokenProvider: TokenProvider = async () => null;

/** Called once from the MSAL provider on app startup. */
export function setTokenProvider(p: TokenProvider): void {
  tokenProvider = p;
}

/**
 * Custom marker error thrown when an admin call cannot complete because
 * authentication is required (no cached token, silent refresh failed, or
 * server returned 401 despite a token). When this is thrown, `auth.tsx`'s
 * tokenProvider has ALREADY kicked off a full-page redirect to Entra — the
 * browser will navigate away within a tick. Admin pages filter this error
 * via `isAuthPending()` so they don't flash an "API 401" banner while the
 * navigation is in flight.
 */

/** Marker error so callers can distinguish "auth pending" from real failures. */
export class AuthRequiredError extends Error {
  status = 401;
  pending = true;
  constructor() {
    super('Authentication required');
    this.name = 'AuthRequiredError';
  }
}

/** True if `e` is the silent "auth pending" marker — admin pages should NOT display this. */
export function isAuthPending(e: unknown): boolean {
  return e instanceof AuthRequiredError;
}

/**
 * Marker error thrown on HTTP 403 — the user is authenticated but lacks the
 * admin role for this endpoint. Non-fatal: unlike AuthRequiredError it does
 * NOT trigger a redirect. Callers/guards can show a clean "not authorized"
 * message instead of a spurious sign-in loop.
 */
export class ForbiddenError extends Error {
  status = 403;
  constructor(message = 'You do not have permission to do that.') {
    super(message);
    this.name = 'ForbiddenError';
  }
}

/** True if `e` is a 403 forbidden marker. */
export function isForbidden(e: unknown): boolean {
  return e instanceof ForbiddenError;
}

function isAdminPath(path: string): boolean {
  // The Function App now requires authentication on EVERY /api/* path
  // (Easy Auth v2 globalValidation.requireAuthentication=true,
  // unauthenticatedClientAction=Return401). The only excluded routes are
  // server-to-server callers (Twilio webhook, Chrome extension key auth,
  // email unsubscribe link, keep-warm /api/ping, bootstrap). None of those
  // are reached from this SPA, so we attach a bearer token to every API
  // call originating here.
  return path.startsWith('/api/');
}

function buildUrl(path: string): string {
  if (!path.startsWith('/')) path = '/' + path;
  return BASE_URL ? `${BASE_URL}${path}` : path;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string>) ?? {})
  };
  if (isAdminPath(path)) {
    const token = await tokenProvider();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      // No token available. The tokenProvider in auth.tsx has already kicked
      // off a full-page redirect to Entra — throw the marker error so admin
      // pages skip showing a red "API 401" banner while the browser is
      // navigating away. `isAuthPending(err)` filters this out.
      throw new AuthRequiredError();
    }
  }

  const url = buildUrl(path);
  const canRetry = isIdempotentMethod(init?.method);

  // Retry loop for cold-start / transient gateway failures on idempotent reads.
  // Non-idempotent methods run exactly once (attempt 0 only).
  for (let attempt = 0; ; attempt++) {
    let res: Response;
    try {
      res = await fetch(url, { ...init, headers });
    } catch (netErr) {
      // Network-level failure (DNS, connection reset, TLS, offline). These are
      // exactly the kind of blip a cold-starting instance produces. Retry
      // idempotent reads before giving up.
      if (canRetry && attempt < TRANSIENT_MAX_ATTEMPTS - 1) {
        await sleep(backoffDelayMs(attempt));
        continue;
      }
      throw netErr;
    }

    if (res.status === 401 && isAdminPath(path)) {
      // Server rejected our bearer token (revoked session / removed app role).
      //
      // Do NOT call `tokenProvider()` again here. When a page has several API
      // calls in flight, a 401 storm meant one extra token acquisition PER
      // failing request — a burst of `authorize` calls that trips Entra's
      // AADSTS50196 loop detector and locks the user out. The provider in
      // auth.tsx is single-flighted and already owns recovery (it kicks off
      // the silent/interactive redirect the next time a token is requested),
      // so just surface the marker error and let the browser navigate.
      throw new AuthRequiredError();
    }
    if (res.status === 403) {
      // Authenticated but not authorized (non-admin hitting an admin endpoint).
      // Non-fatal — surface a clean message, never a redirect loop.
      const text = await res.text().catch(() => '');
      let message = 'You do not have permission to do that.';
      try {
        const parsed = text ? JSON.parse(text) : null;
        if (typeof parsed?.message === 'string') message = parsed.message;
        else if (typeof parsed?.error === 'string') message = parsed.error;
      } catch {
        /* keep default */
      }
      throw new ForbiddenError(message);
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      // Cold-start / transient gateway failure on an idempotent read → wait a
      // beat and try again (the instance is still warming up).
      if (canRetry && attempt < TRANSIENT_MAX_ATTEMPTS - 1 && isTransientColdStart(res.status, text)) {
        await sleep(backoffDelayMs(attempt));
        continue;
      }
      const err = new Error(`API ${res.status}: ${text || res.statusText}`) as Error & { status?: number };
      err.status = res.status;
      throw err;
    }
    if (res.status === 204) return undefined as T;
    const ct = res.headers.get('content-type') ?? '';
    return (ct.includes('application/json') ? await res.json() : ((await res.text()) as unknown)) as T;
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' })
};

export interface GeocodeResult {
  lat: number;
  lng: number;
  formattedAddress: string;
}

/**
 * Resolve a free-form address to coordinates via the public `/api/geocode`
 * endpoint. Returns `null` when the address can't be matched (HTTP 404) so
 * callers can show a "couldn't find that address" hint without try/catch.
 * Other errors (network, 5xx) are re-thrown.
 */
export async function geocode(query: string): Promise<GeocodeResult | null> {
  const q = query.trim();
  if (!q) return null;
  try {
    return await api.get<GeocodeResult>(`/api/geocode?q=${encodeURIComponent(q)}`);
  } catch (e) {
    if (e instanceof Error && 'status' in e && (e as { status?: number }).status === 404) {
      return null;
    }
    throw e;
  }
}
